import { Connection } from "@solana/web3.js";
import { AnchorProvider, Program, setProvider } from "@coral-xyz/anchor";
import type { Wallet } from "@coral-xyz/anchor/dist/cjs/provider";

import {
  resolveBrowserCircuitPaths,
  resolveCircuitPathsFromBase,
  type CircuitPaths,
} from "../circuits/constants";
import type { Network } from "./constants";
import {
  getIdlForNetwork,
  getIndexerUrlForNetwork,
  getProgramIdForNetwork,
  resolveNetworkFromRpcEndpoint,
} from "./constants";
import { fetchRelayersForNetwork, type Relayer } from "./relayers";

export interface ArcaneSdkInitOptions {
  /** Range API key (frontend: VITE_RANGE_API_KEY). */
  rangeApiKey: string;
  /** Wallet for Anchor provider transactions. */
  wallet: Wallet;
  /**
   * Solana JSON-RPC endpoint (Node/scripts). Omit when passing {@link connection}
   * from `@solana/wallet-adapter-react` (`useConnection()`).
   */
  rpcEndpoint?: string;
  /** Existing connection (e.g. `useConnection()` in React). */
  connection?: Connection;
  /**
   * Cluster override when the RPC URL does not contain `devnet` / `mainnet` hints
   * (common with private RPC providers).
   */
  network: Network;
  /**
   * Base URL/path for withdraw circuit artifacts in the browser.
   * Defaults to `/node_modules/<pkg>/dist/circuits/` on localhost and the
   * pinned, SHA-256-verified jsDelivr CDN in production (domain-independent).
   * Set this to self-host instead, e.g. `"/circuits/"` (verified, with CDN fallback)
   * or `"https://cdn.example.com/arcane/circuits/"`.
   */
  circuitPathsBase?: string;
}

export interface ArcaneSdkRuntimeConfig {
  rangeApiKey: string;
  rpcEndpoint: string;
  /** Cluster inferred from {@link rpcEndpoint}. */
  network: Network;
  programId: string;
  /** Active relayers for {@link network}, loaded at SDK initialization. */
  relayers: Relayer[];
  connection: Connection;
  wallet: Wallet;
  program: Program;
  /** Set by {@link ArcaneSDK.getEncryptionKey}; required for on-chain note backup. */
  encryptKey?: Uint8Array;
  /** Resolved withdraw circuit wasm/zkey URLs for browser proof generation. */
  circuitPaths?: CircuitPaths;
}

let runtimeConfig: ArcaneSdkRuntimeConfig | null = null;
const runtimeContextStack: ArcaneSdkRuntimeConfig[] = [];

function resolveInitConnection(
  options: ArcaneSdkInitOptions
): { connection: Connection; rpcEndpoint: string } {
  if (options.connection) {
    return {
      connection: options.connection,
      rpcEndpoint: options.connection.rpcEndpoint,
    };
  }
  if (options.rpcEndpoint) {
    return {
      connection: new Connection(options.rpcEndpoint),
      rpcEndpoint: options.rpcEndpoint,
    };
  }
  throw new Error(
    "arcane-sdk: provide either rpcEndpoint or connection in ArcaneSDK.create()"
  );
}

/** Build runtime config without mutating module state (for {@link ArcaneSDK}). */
export async function createRuntimeConfig(
  options: ArcaneSdkInitOptions
): Promise<ArcaneSdkRuntimeConfig> {
  const { connection, rpcEndpoint } = resolveInitConnection(options);
  const network =
    options.network;
  const indexerUrl = getIndexerUrlForNetwork(network);
  const relayers = await fetchRelayersForNetwork(network, indexerUrl);
  const provider = new AnchorProvider(connection, options.wallet, {
    commitment: "confirmed",
  });
  setProvider(provider);
  const program = new Program(getIdlForNetwork(network), provider);
  const circuitPaths =
    options.circuitPathsBase !== undefined
      ? resolveCircuitPathsFromBase(options.circuitPathsBase)
      : resolveBrowserCircuitPaths() ?? undefined;

  return {
    rangeApiKey: options.rangeApiKey,
    rpcEndpoint,
    network,
    programId: getProgramIdForNetwork(network),
    relayers,
    connection,
    wallet: options.wallet,
    program,
    circuitPaths,
  };
}

export async function init(
  options: ArcaneSdkInitOptions
): Promise<ArcaneSdkRuntimeConfig> {
  runtimeConfig = await createRuntimeConfig(options);
  return runtimeConfig;
}

export function pushRuntimeContext(config: ArcaneSdkRuntimeConfig): void {
  runtimeContextStack.push(config);
}

export function popRuntimeContext(): void {
  runtimeContextStack.pop();
}

export function getConfig(): ArcaneSdkRuntimeConfig {
  const active = runtimeContextStack.at(-1);
  if (active) {
    return active;
  }
  if (!runtimeConfig) {
    throw new Error(
      "arcane-sdk: call init() or create an ArcaneSDK instance before using SDK functions"
    );
  }
  return runtimeConfig;
}

export function isInitialized(): boolean {
  return runtimeConfig !== null;
}

export function resetConfig(): void {
  runtimeConfig = null;
}

export type { Relayer, Wallet };
